// src/components/shared/modals/ModalFooter.tsx
// Shared modal footer with cancel/submit button pattern.
// UI Phase 2.1: renders the kit's Button primitives. The legacy color
// variants map onto intents: blue/green → primary (the action), red → danger
// (destructive/irreversible), amber → warning (caution, non-destructive).
// Prop API unchanged.
import React from 'react';
import { type LucideIcon } from 'lucide-react';
import { Button } from '../../ui';
import type { ButtonIntent } from '../../ui';

const VARIANT_TO_INTENT: Record<string, ButtonIntent> = {
  blue: 'primary',
  green: 'primary',
  red: 'danger',
  amber: 'warning',
};

interface ModalFooterProps {
  onCancel: () => void;
  onSubmit?: () => void;
  cancelText?: string;
  submitText?: string;
  loadingText?: string;
  variant?: 'blue' | 'red' | 'amber' | 'green';
  isLoading?: boolean;
  disabled?: boolean;
  submitType?: 'button' | 'submit';
  submitIcon?: LucideIcon;
  cancelDisabled?: boolean;
}

const ModalFooter = ({
  onCancel,
  onSubmit,
  cancelText = 'Cancel',
  submitText = 'Confirm',
  loadingText = 'Loading...',
  variant = 'blue',
  isLoading = false,
  disabled = false,
  submitType = 'button',
  submitIcon: SubmitIcon,
  cancelDisabled = false,
}: ModalFooterProps) => (
  <div className="px-6 py-4 border-t border-line-soft flex justify-end gap-3">
    <Button
      type="button"
      intent="secondary"
      onClick={onCancel}
      disabled={isLoading || cancelDisabled}
    >
      {cancelText}
    </Button>
    <Button
      type={submitType}
      intent={VARIANT_TO_INTENT[variant] || 'primary'}
      onClick={submitType === 'button' ? onSubmit : undefined}
      disabled={disabled}
      isLoading={isLoading}
      icon={SubmitIcon ? <SubmitIcon className="h-4 w-4" aria-hidden="true" /> : undefined}
    >
      {isLoading ? loadingText : submitText}
    </Button>
  </div>
);

export default ModalFooter;
